import React, { useEffect, useState } from 'react';
import { getCurrentUser, signInWithRedirect, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { getUserData } from '../services/userService';
import AuthCallback from './AuthCallback';
import Logo from './Logo';

function AuthHandler({ onLoginSuccess, children }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);

  useEffect(() => {
    // Detectar si estamos en un callback de OAuth
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthCode = urlParams.has('code');
    const hasAuthState = urlParams.has('state');
    
    if (hasAuthCode && hasAuthState) {
      //console.log('🔄 Detectado callback de OAuth, procesando...');
      setIsProcessingCallback(true);
      // Dar tiempo a Amplify para procesar el callback
      setTimeout(() => {
        checkAuthState();
      }, 2000);
    } else {
      checkAuthState();
    }
  }, []);

  const checkAuthState = async () => {
    try {
      //console.log('🔍 Verificando estado de autenticación...');
      
      // Intentar varias veces si estamos procesando un callback
      let retries = isProcessingCallback ? 3 : 1;
      let currentUser = null;
      let session = null;
      
      for (let i = 0; i < retries; i++) {
        try {
          currentUser = await getCurrentUser();
          session = await fetchAuthSession();
          if (currentUser && session) break;
        } catch (e) {
          if (i < retries - 1) {
            console.log(`🔄 Intento ${i + 1} fallido, reintentando...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      if (currentUser && session) {
        //console.log('✅ Usuario autenticado:', currentUser);
        //console.log('🎫 Sesión:', session);
        
        try {
          // Obtener datos completos del usuario desde tu API
          const completeUserData = await getUserData(currentUser.username);
          
          //console.log('🔍 AuthHandler - Datos completos de la API:');
          //console.log('👤 completeUserData.item.user_name:', completeUserData.item?.user_name);
          //console.log('🆔 currentUser.username:', currentUser.username);
          //console.log('📦 Datos completos de la API:', completeUserData);
          
          // 🎯 Extraer datos del objeto 'item' que viene de la API
          const apiData = completeUserData.item || {};
          
          // Combinar datos de Cognito con los datos de tu API
          const userData = {
            user_id: currentUser.username,
            user_email: currentUser.signInDetails?.loginId || apiData.user_email || '',
            user_name: apiData.user_name || currentUser.username,
            tenant_id: apiData.tenant_id || '',
            user_brand: apiData.user_brand || '',
            company_name: apiData.company_name || '',
            store_name: apiData.store_name || '',
            store_city: apiData.store_city || '',
            user_country: apiData.user_country || '',
            user_type: apiData.user_type || ''
          };
          
          //console.log('🎯 AuthHandler - Datos finales para el contexto:');
          //console.log('👤 userData.user_name:', userData.user_name);
          console.log('📦 userData completo:', userData);

          // Guardar en localStorage
          localStorage.setItem('tenant_id', userData.tenant_id);
          localStorage.setItem('user_brand', userData.user_brand);
          localStorage.setItem('user_email', userData.user_email);
          localStorage.setItem('user_id', userData.user_id);
          localStorage.setItem('user_name', userData.user_name);
          localStorage.setItem('company_name', userData.company_name);
          localStorage.setItem('store_name', userData.store_name);
          localStorage.setItem('store_city', userData.store_city);
          localStorage.setItem('user_country', userData.user_country);
          localStorage.setItem('user_type', userData.user_type);

          setUser(currentUser);
          setIsAuthenticated(true);
          setIsProcessingCallback(false);
          
          // Limpiar URL de parámetros OAuth
          if (window.location.search.includes('code=')) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          
          onLoginSuccess(userData);
          
        } catch (apiError) {
          console.warn('⚠️ No se pudieron obtener los datos completos del usuario:', apiError);
          
          // Usar solo los datos básicos de Cognito si falla la API
          const basicUserData = {
            user_id: currentUser.username,
            user_email: currentUser.signInDetails?.loginId || '',
            user_name: currentUser.username,
            tenant_id: '',
            user_brand: '',
            company_name: '',
            store_name: '',
            store_city: '',
            user_type: ''
          };

          setUser(currentUser);
          setIsAuthenticated(true);
          setIsProcessingCallback(false);
          
          // Limpiar URL de parámetros OAuth
          if (window.location.search.includes('code=')) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          
          onLoginSuccess(basicUserData);
        }
      } else {
        //console.log('❌ Usuario no autenticado');
        setIsAuthenticated(false);
        setIsProcessingCallback(false);
      }
    } catch (error) {
      //console.log('❌ Error verificando autenticación:', error);
      setIsAuthenticated(false);
      setIsProcessingCallback(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      await signInWithRedirect({
        provider: 'Cognito'
      });
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
    }
  };

  const handleLogout = async () => {
    try {
      //console.log('🚪 AuthHandler: Cerrando sesión en Cognito...');
      await signOut();
      setIsAuthenticated(false);
      setUser(null);
      
      // Limpiar localStorage
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('user_brand');
      localStorage.removeItem('user_email');
      localStorage.removeItem('user_id');
      localStorage.removeItem('user_name');
      localStorage.removeItem('company_name');
      localStorage.removeItem('store_name');
      localStorage.removeItem('store_city');
      localStorage.removeItem('user_country');
      localStorage.removeItem('user_type');
      
      //console.log('✅ AuthHandler: Sesión cerrada correctamente');
    } catch (error) {
      console.error('❌ AuthHandler: Error al cerrar sesión:', error);
    }
  };

  if (isProcessingCallback) {
    return <AuthCallback onAuthSuccess={() => checkAuthState()} />;
  }

  if (isLoading) {
    return (
      <div className="login-container animate-fade-in">
        <div className="login-card card">
          <div className="login-header">
            <Logo variant="full" size="large" />
            <h2 className="card-title">Verificando autenticación...</h2>
            <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      {!isAuthenticated && (
        <div style={{ display: 'none' }}>
          <button onClick={handleLogin}>Login</button>
          <button onClick={handleLogout}>Logout</button>
        </div>
      )}
    </>
  );
}

export default AuthHandler; 